import React, {
  useMemo, useEffect, useState, useCallback
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { media, mediaMUI } from 'theme/styled-theme';
import StyledButtonRounded from 'components/MuiComponents/RoundedButton';
import SingleDatePickerWrapper from 'components/SingleDatePicker/SingleDatePickerLoadable';
import arrowRightIcon from 'assets/icon-arrow-white.svg';
import isInclusivelyAfterDay from 'react-dates/lib/utils/isInclusivelyAfterDay';
import getMinBookingDate from 'utils/getMinimumBookingDate';
import moment from 'moment-timezone'; // this is a expensive module, consider replacing it
import { useTranslation } from 'react-i18next';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { sendEvent } from 'utils/gtag';
import PageSectionHeaders from '../PageSectionHeaders/Loadable';
import patternUrl from '../../assets/pattern-calender.png';

/* -------------------------------------------------------------------------- */
/*                              styled components                             */
/* -------------------------------------------------------------------------- */
const Root = styled.div`
  position: relative;
  font-family: ${props => props.theme.fontSourceSansPro};
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 0 1rem;
  background-color: #ccecff;
  background: url("${props => props.pattern}") top left/200px 200px;
`;

const ContentWrap = styled.div`
  width: 100%;
  max-width: 684px;
  background: #FFFFFF;
  box-shadow: 0 16px 40px 0 rgba(6,28,63,0.20);
  border-radius: 2px;
  padding: 2rem 1.5rem;
  margin: 0 auto 4.5rem;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;

  ${media.forPhoneOnly`
    padding: 1.5rem 1rem;
    flex-direction: column;
  `}
`;

const Date = styled.div`
  flex: 1 0 0;

  ${media.forPhoneOnly`
    width: 100%;
    margin-bottom: 0.75rem;

    .SingleDatePicker, .SingleDatePickerInput {
      width: 100%;
    }
  `}

  .DateInput {
    width: 100%;

    input {
      padding: 11px;
      text-align: left;
    }
  }
`;

const Arrow = styled.div`
  ${media.forPhoneOnly`
    display: none;
  `}
  & > div {
    padding: 13px;
  }

  svg polygon {
    fill: #595959;
  }
`;

/* -------------------------------------------------------------------------- */
/*                               React Component                              */
/* -------------------------------------------------------------------------- */
const CountryLandingSection = ({
  onButtonClick
}) => {
  const { t } = useTranslation('new-version');
  const [svg, setSvg] = useState(null);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  useEffect(() => {
    fetch(arrowRightIcon)
      .then(res => res.text())
      .then(setSvg);
  }, []);

  const changeStartDate = useCallback(date => {
    sendEvent({ action: 'Set Cutom Tour Start Date', category: 'engagement' });
    if (!endDate) {
      setStartDate(date.format('YYYY-MM-DD'));
    } else {
      // valueOf is moment method of geting UNIX timestamp
      const startUnix = date.valueOf();
      const endUnix = endDate.valueOf();

      // When saving time, make sure it's trim off to YYYY-MM-DD, ignore timezon, hours, etc..
      setStartDate(date.format('YYYY-MM-DD'));
      setEndDate(startUnix > endUnix ? null : endDate);
    }
  }, [endDate]);
  const changeEndDate = useCallback(date => {
    setEndDate(date);
    sendEvent({ action: 'Set Cutom Tour End Date', category: 'engagement' });
  }, []);
  const minimumDate = useCallback(
    day => !isInclusivelyAfterDay(day, moment(startDate)),
    [startDate],
  );
  const matchePhone = useMediaQuery(mediaMUI.forPhoneOnly);
  const buttonStyles = useMemo(() => ({
    root: {
      color: '#ffffff',
      borderColor: 'none',
      backgroundColor: '#00B33B',
      marginLeft: matchePhone ? 0 : '1rem',
    }
  }), [matchePhone]);

  const onCustomTourButtonClick = useCallback(e => {
    e.stopPropagation();
    e.preventDefault();

    sendEvent({ action: 'Click Cutom Tour Action Button', category: 'navigation' });

    /**
     * 4 cases
     *
     * a. both exist, set as usual
     * b. only startDate exist, set endDate with startDate
     * c. only startDate exist, use endDate as startDate
     * d. both empty, set default day
     *
     */
    if (startDate && endDate) {
      onButtonClick({ startDate, endDate });
    } else if (startDate && !endDate) {
      // When saving time, make sure it's trim off to YYYY-MM-DD, ignore timezon, hours, etc..
      onButtonClick({ startDate, endDate: startDate });
    } else if (!startDate && endDate) {
      onButtonClick({ startDate: endDate, endDate });
    } else {
      const defaultDate = getMinBookingDate();
      return onButtonClick({ startDate: defaultDate, endDate: defaultDate });
    }
  }, [startDate, endDate]);

  return (
    <Root pattern={patternUrl}>
      <PageSectionHeaders
        title={t('CITY.CUSTOMIZE.HEADING')}
        subtitle={t('CITY.CUSTOMIZE.SUBHEADING')}
        center
      />
      <ContentWrap>
        <Date>
          <SingleDatePickerWrapper
            date={startDate}
            placeholder="Start Date"
            onTripDateChange={changeStartDate}
          />
        </Date>

        <Arrow>
          {/* eslint-disable-next-line react/no-danger */}
          <div dangerouslySetInnerHTML={{ __html: svg }} />
        </Arrow>

        <Date>
          <SingleDatePickerWrapper
            date={endDate}
            placeholder="End Date"
            onTripDateChange={changeEndDate}
            isOutsideRange={minimumDate}
          />
        </Date>

        <StyledButtonRounded
          variant="contained"
          overwritestyles={buttonStyles}
          onClick={onCustomTourButtonClick}
        >
          {t('CITY.CUSTOMIZE.ACTION_BUTTON')}
        </StyledButtonRounded>
      </ContentWrap>
    </Root>
  );
};

CountryLandingSection.propTypes = {
  onButtonClick: PropTypes.func,
};

CountryLandingSection.defaultProps = {
  onButtonClick: () => {},
};

export default CountryLandingSection;
